import React, { useEffect, useState } from "react";

import WPDropdown from "../../packages/WPDropdown";

import { getCIPSequenceIdFromSequenceTextArr } from "./cipSequence.utils";
import CIPSequenceChemicalSection from "./CIPSequenceChemicalSection";
import { CIP_SEQUENCE_OPTIONS } from "./defaultCIPInformationFields.config";

const SEQUENCE_GROUP_BY_VALUE = {
  acid: "acid",
  "acid solution": "acid",
  alkali: "alkali",
  "alkali solution": "alkali",
};

const getBlockedValuesByFirstSelection = (firstSelection) => {
  const normalizedSelection = firstSelection?.toLowerCase?.();
  const group = SEQUENCE_GROUP_BY_VALUE[normalizedSelection];

  if (!group) {
    return new Set();
  }

  return new Set(
    Object.keys(SEQUENCE_GROUP_BY_VALUE).filter(
      (value) => SEQUENCE_GROUP_BY_VALUE[value] === group,
    ),
  );
};

const CIPSequence = ({
  cipSequenceTextArr,
  setCIPSequenceTextArr,
  setChangedCIPValues,
  errors,
  handleInputChange,
  onFieldChange,
  onMandatoryValidationChange,
  disabled,
  existingCIPState,
}) => {
  const [mandatoryValidationBySection, setMandatoryValidationBySection] =
    useState({});

  const getChemicalSelectionKeysForType = (chemicalType) => {
    chemicalType = chemicalType?.toLowerCase();
    if (chemicalType === "acid solution") {
      return ["acid", "additive_acid"];
    }

    if (chemicalType === "alkali solution") {
      return ["alkali", "additive_alkali"];
    }

    return [chemicalType];
  };

  const handleCIPSequenceChange = (index) => (option) => {
    const selectedValue = option?.value;
    if (!selectedValue) return;

    setCIPSequenceTextArr((prev) => {
      const next = [...prev];
      next[index] = selectedValue;

      const blockedByFirstSelection = getBlockedValuesByFirstSelection(
        next?.[0],
      );

      // reset second dropdown to none when first dropdown changes into same group
      if (
        index === 0 &&
        (next[1] === selectedValue || blockedByFirstSelection.has(next[1]))
      ) {
        next[1] = "none";
      }

      setChangedCIPValues((prevChangedValues) => ({
        ...prevChangedValues,
        cip_sequence: getCIPSequenceIdFromSequenceTextArr(next),
      }));

      return next;
    });
  };

  const getCIPSequenceDropdownOptions = (index) => {
    const selectedValuesBeforeIndex = new Set(
      cipSequenceTextArr.slice(0, index),
    );
    const blockedByFirstSelection = getBlockedValuesByFirstSelection(
      cipSequenceTextArr?.[0],
    );

    const options = CIP_SEQUENCE_OPTIONS.filter(
      (option) =>
        !selectedValuesBeforeIndex.has(option.value) &&
        !(index > 0 && blockedByFirstSelection.has(option.value)),
    );

    if (index > 0) {
      return [...options, { label: "none", value: "none" }];
    }

    return options;
  };

  useEffect(() => {
    if (!onMandatoryValidationChange) {
      return;
    }

    const hasMissingRequiredFields = cipSequenceTextArr.some(
      (chemicalType) =>
        chemicalType !== "none" &&
        mandatoryValidationBySection[chemicalType] !== false,
    );

    onMandatoryValidationChange(hasMissingRequiredFields);
  }, [
    cipSequenceTextArr,
    mandatoryValidationBySection,
    onMandatoryValidationChange,
  ]);

  return (
    <div className="cip-section-content-wrapper">
      {cipSequenceTextArr.map((chemicalType, index) => {
        const chemicalSelection = getChemicalSelectionKeysForType(chemicalType);

        return (
          <div key={`cip-sequence-item-${index}`} className="cip-sequence-item">
            <div className="cip-sequence-number">{index + 1}</div>
            <div className="cip-sequence-content">
              <WPDropdown
                id={`cip_chemical_selection_dropdown_${index}`}
                value={chemicalType}
                options={getCIPSequenceDropdownOptions(index)}
                onChange={handleCIPSequenceChange(index)}
                customClassName="cip_chemical_selection_dropdown"
                disabled={disabled}
              />
              <CIPSequenceChemicalSection
                defaultCIPKeys={chemicalSelection}
                errors={errors}
                handleInputChange={handleInputChange}
                onFieldChange={onFieldChange}
                onMandatoryValidationChange={(hasMissingRequiredFields) =>
                  setMandatoryValidationBySection((previous) => ({
                    ...previous,
                    [chemicalType]: hasMissingRequiredFields,
                  }))
                }
                disabled={disabled}
                existingCIPState={existingCIPState}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(CIPSequence);
